import { useSetAtom } from "jotai";
import { signOut } from "@/lib/auth-client";
import { useSession } from "@/hooks/auth-hooks";
import { useQueryClient } from "@tanstack/react-query";
import { useNotification } from "@/hooks/useNotification";
import { isLoggingOutAtom } from "@/state/dialogs";
import { initialOnboarding, onboardingAtom } from "@/state/onboarding";
import { useTranslation } from "@/hooks/useTranslation";

type useLogoutInvalidateProps = {
  logout: () => Promise<void>;
};

/**
 * Invalidate everything in the query cache and sign out using Better Auth
 */
export const useLogoutInvalidate = (): useLogoutInvalidateProps => {
  const setIsLoggingOut = useSetAtom(isLoggingOutAtom);
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();
  const { refetch } = useSession();
  const { t } = useTranslation();

  const setOnboarding = useSetAtom(onboardingAtom);

  const logout = async () => {
    try {
      // Set loading state to prevent UI updates during logout
      setIsLoggingOut(true);

      // 1. Immediately invalidate user-related queries to prevent UI flicker
      queryClient.removeQueries({ queryKey: ["user"] });

      // 2. Sign out from Better Auth
      await signOut();

      // 3. Clear all React Query caches to remove any stale data
      await queryClient.clear();

      // 4. Force a refetch of the user session to ensure it's properly cleared
      await refetch();

      // Reset onboarding state
      setOnboarding(initialOnboarding);

      setIsLoggingOut(false);
    } catch (error) {
      console.error("Error during logout:", error);
      showNotification({
        type: "error",
        message: t("hooks.error.logout_issue"),
      });
    } finally {
      setIsLoggingOut(false);
    }
  };

  return {
    logout,
  };
};
